import { useMutation, useQuery } from "@apollo/client";
import { AnimatePresence } from "framer-motion";
import { take } from "lodash";
import moment from "moment";
import { useCallback, useState } from "react";
import {
  DeleteDraftMeetingNewPageMutationMutation,
  DeleteDraftMeetingNewPageMutationMutationVariables,
  GetUnscheduledMeetingsQueryQuery,
  GetUnscheduledMeetingsQueryQueryVariables,
} from "types/graphql-schema";

import Calendar from "@apps/calendar/calendar";
import deleteDraftMeetingMutation from "@apps/meeting-new/graphql/delete-draft-meeting-mutation";
import useUiPreferenceCache, {
  MeetingsViewEnum,
} from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import { currentUserVar, successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import AppLink from "@components/link/link";
import { useLink } from "@components/link/link-helpers";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import MotionDiv from "@components/motion/motion-div";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon-helpers";
import {
  assertEdgesNonNull,
  getUrl,
  getUrlUsingTypename,
} from "@helpers/helpers";

import MeetingOverview from "./components/meetings-overview";
import getUnscheduledMeetingsQuery from "./graphql/get-unscheduled-meetings-query";

const MeetingsPanel = () => {
  const currentUser = currentUserVar();
  const [selectedDay, setSelectedDay] = useState(moment().format("YYYY-MM-DD"));
  const [forceTodayScroll, setForceTodayScroll] = useState(0);
  const [showUnscheduledMeetings, setShowUnscheduledMeetings] = useState(false);
  const link = useLink();
  const { data } = useQuery<
    GetUnscheduledMeetingsQueryQuery,
    GetUnscheduledMeetingsQueryQueryVariables
  >(getUnscheduledMeetingsQuery, {
    variables: { forUserId: currentUser.id },
  });
  const unscheduledMeetings = data?.unscheduledMeetings
    ? assertEdgesNonNull(data.unscheduledMeetings)
    : [];

  const { saveUiPreference, uiPreferenceCache } = useUiPreferenceCache();

  const handleChangeSidebarMeetingId = (
    meetingId: number,
    meetingGroupId: number,
  ) => {
    const url = getUrl({ meetingGroupId, meetingId });
    link.redirect(url);
  };

  const [deleteDraftMeeting] = useMutation<
    DeleteDraftMeetingNewPageMutationMutation,
    DeleteDraftMeetingNewPageMutationMutationVariables
  >(deleteDraftMeetingMutation);

  const handleClickDeleteDraftMeeting = useCallback(
    (meetingId: number) => {
      if (
        window.confirm(
          "Are you sure you want to delete this unscheduled meeting?",
        )
      ) {
        deleteDraftMeeting({
          variables: { meetingId },
          refetchQueries: [getUnscheduledMeetingsQuery],
          onCompleted: () => {
            successNotificationVar({ title: "Meeting discarded." });
          },
        });
      }
    },
    [deleteDraftMeeting],
  );

  return (
    <div className="text-gray-700 w-72 border-r flex flex-col">
      <div className="border-b bg-white flex gap-1 justify-between px-4 h-14 items-center">
        <div className="flex items-center gap-2">
          Meetings
          <Button
            size={ButtonSize.medium}
            theme={
              uiPreferenceCache.meetingsView === MeetingsViewEnum.overview
                ? buttonTheme.lightGray
                : buttonTheme.default
            }
            text="List"
            className="ml-2"
            onClick={() =>
              saveUiPreference({ meetingsView: MeetingsViewEnum.overview })
            }
          />
          <Button
            size={ButtonSize.medium}
            theme={
              uiPreferenceCache.meetingsView === MeetingsViewEnum.calendar
                ? buttonTheme.lightGray
                : buttonTheme.default
            }
            text="Cal"
            onClick={() =>
              saveUiPreference({ meetingsView: MeetingsViewEnum.calendar })
            }
          />
        </div>
        <Button
          tooltip="Jump to Today"
          icon={getTopicflowIcon("today")}
          theme={buttonTheme.iconGray}
          size={ButtonSize.medium}
          disabled={
            uiPreferenceCache.meetingsView === MeetingsViewEnum.calendar
              ? moment(selectedDay).isSame(moment(), "day")
              : false
          }
          onClick={() => {
            setSelectedDay(moment().format("YYYY-MM-DD"));
            setForceTodayScroll(Date.now());
          }}
        />
      </div>
      {uiPreferenceCache.meetingsView === MeetingsViewEnum.calendar && (
        <div className="border-b bg-white flex gap-1 justify-between px-4 h-14 items-center">
          {moment(selectedDay).format("ddd, MMM Do")}
          <div className="flex items-center gap-1">
            <Button
              icon={getTopicflowIcon("chevronLeft")}
              onClick={() =>
                setSelectedDay(
                  moment(selectedDay).subtract(1, "day").format("YYYY-MM-DD"),
                )
              }
              size={ButtonSize.medium}
              theme={buttonTheme.text}
            />
            <Button
              icon={getTopicflowIcon("chevronRight")}
              onClick={() =>
                setSelectedDay(
                  moment(selectedDay).add(1, "day").format("YYYY-MM-DD"),
                )
              }
              size={ButtonSize.medium}
              theme={buttonTheme.text}
            />
          </div>
        </div>
      )}
      <div className="relative flex-1 bg-white">
        {uiPreferenceCache.meetingsView === MeetingsViewEnum.overview && (
          <MeetingOverview
            selectedDay={selectedDay}
            onClickMeetingLink={handleChangeSidebarMeetingId}
            forceTodayScroll={forceTodayScroll}
          />
        )}
        {uiPreferenceCache.meetingsView === MeetingsViewEnum.calendar && (
          <Calendar
            selectedDay={selectedDay}
            onClickMeetingLink={handleChangeSidebarMeetingId}
          />
        )}
      </div>
      {unscheduledMeetings.length > 0 && (
        <div className="border-t px-6 py-3 bg-white">
          <div className="flex items-center justify-between gap-1">
            <div className="text-xs font-medium">
              Unscheduled meetings ({unscheduledMeetings.length})
            </div>
            <Button
              icon={getTopicflowIcon("edit")}
              size={ButtonSize.medium}
              theme={buttonTheme.iconGray}
              onClick={() => setShowUnscheduledMeetings(true)}
            />
          </div>
          {showUnscheduledMeetings && (
            <Modal onClose={() => setShowUnscheduledMeetings(false)} alignedTop>
              <div className="p-6 flex flex-col gap-4">
                <ModalTitle onClose={() => setShowUnscheduledMeetings(false)}>
                  Unscheduled Meetings
                </ModalTitle>
                <div className="flex flex-col text-gray-700 border rounded-md divide-y">
                  {unscheduledMeetings.map((unscheduledMeeting) => (
                    <div
                      className="px-4 py-2 hover:bg-gray-50 flex items-center min-w-0 overflow-hidden"
                      key={unscheduledMeeting.id}
                    >
                      {unscheduledMeeting.meeting ? (
                        <AppLink
                          className="flex-1 truncate"
                          to={getUrlUsingTypename(unscheduledMeeting.meeting)}
                          onClick={(e) => {
                            e.preventDefault();
                            if (unscheduledMeeting.meeting) {
                              handleChangeSidebarMeetingId(
                                unscheduledMeeting.meeting.id,
                                unscheduledMeeting.meeting.meetingGroupId,
                              );
                              setShowUnscheduledMeetings(false);
                            }
                          }}
                        >
                          {unscheduledMeeting.meeting?.title}
                        </AppLink>
                      ) : (
                        <span className="flex-1">Unscheduled Meeting</span>
                      )}
                      <div>
                        <Button
                          icon={getTopicflowIcon("trash")}
                          theme={buttonTheme.iconGray}
                          size={ButtonSize.medium}
                          onClick={() => {
                            if (unscheduledMeeting.meeting) {
                              handleClickDeleteDraftMeeting(
                                unscheduledMeeting.meeting.id,
                              );
                            }
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Modal>
          )}
          <div className="flex flex-col">
            <AnimatePresence>
              {take(unscheduledMeetings, 3).map((unscheduledMeeting) => {
                return (
                  <MotionDiv
                    key={unscheduledMeeting.id}
                    className=""
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <div className="rounded-md hover:bg-gray-100 px-2 py-1 -mx-2 text-gray-700">
                      {unscheduledMeeting.meeting ? (
                        <AppLink
                          className="w-full flex min-w-0 gap-2 items-center"
                          to={getUrlUsingTypename(unscheduledMeeting.meeting)}
                          onClick={(e) => {
                            e.preventDefault();
                            if (unscheduledMeeting.meeting) {
                              handleChangeSidebarMeetingId(
                                unscheduledMeeting.meeting.id,
                                unscheduledMeeting.meeting.meetingGroupId,
                              );
                            }
                          }}
                        >
                          <span className="flex-1 truncate">
                            {unscheduledMeeting.meeting?.title}
                          </span>
                          <span className="text-gray-500">-</span>
                        </AppLink>
                      ) : (
                        <div className="w-full flex gap-2 items-center">
                          <span className="flex-1">Unscheduled Meeting</span>
                          <span className="text-gray-500">-</span>
                        </div>
                      )}
                    </div>
                  </MotionDiv>
                );
              })}
            </AnimatePresence>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeetingsPanel;
